import { LDFlagSet } from 'launchdarkly-react-client-sdk';
import { memoize } from 'micro-memoize';

import { Integration } from '@futrli-sdk/app-store';
import { checkNavPermission, ROUTES } from '@futrli/shared-services';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';

import { ApiView, ApiViewState, getDefaultApiViewState } from '../../../core/ApiView';
import { queryClient } from '../../../core/queryClient';
import { currentUserQuery, CurrentUserResult } from '../../../queries/currentUser';
import { FetchConsolidationsResult, consolidationsQuery } from '../../../queries/consolidations';
import { getUpdatedWorkspacesListItems } from '../../../operations/workspaces/utils/utils';
import { integrationsQuery } from '../../../queries/integrations';
import { ExtendedWorkspaceListItem, processWorkspace } from '../../../operations/workspaces/processors/workspacesList';
import { workspaceOrgDatasourcesInSyncing } from '../../../operations/workspaces/processors/workspaceOrgsInSyncing';
import {
  workspacesDatasourcePollDataQuery,
  workspacesDatasourcePollDataQueryConfig
} from '../../../operations/workspaces/queries/workspacesDatasourcePollDataQuery';
import {
  workspacesListingQuery,
  workspacesListingQueryKey
} from '../../../operations/workspaces/queries/workspacesListingQuery';
export interface WorkspaceListViewState extends ApiViewState {
  consolidations?: FetchConsolidationsResult;
  currentUser?: CurrentUserResult;
  hasMembersPermission: boolean;
  integrations?: Integration[];
  workspace?: ExtendedWorkspaceListItem;
  workspaces?: WorkspacesListingItem[];
}

export class WorkspaceListView extends ApiView {
  protected currentUserObs: ReturnType<typeof currentUserQuery>;
  protected consolidationsObs: ReturnType<typeof consolidationsQuery>;
  protected integrationsObs: ReturnType<typeof integrationsQuery>;
  protected workspacesObs: ReturnType<typeof workspacesListingQuery>;
  protected workspacesDSPollDataObs: ReturnType<typeof workspacesDatasourcePollDataQuery>;

  protected appliedPollDataKey = '';
  protected loadedPollDatasourceIdsKey = '';
  protected memoizedProcessWorkspace: typeof processWorkspace;
  protected memoizedOrgsInSyncing: typeof workspaceOrgDatasourcesInSyncing;

  public state: WorkspaceListViewState = {
    ...getDefaultApiViewState(),
    hasMembersPermission: false,
    workspace: {} as ExtendedWorkspaceListItem
  };

  constructor(protected flags: LDFlagSet, protected workspaceId: string, protected pollingEnabled = false) {
    super();

    // Observers
    this.currentUserObs = this.addQuery(currentUserQuery());
    this.consolidationsObs = this.addQuery(consolidationsQuery(workspaceId));
    this.integrationsObs = this.addQuery(integrationsQuery());
    this.workspacesObs = this.addQuery(workspacesListingQuery());
    this.workspacesDSPollDataObs = this.addQuery(workspacesDatasourcePollDataQuery([]));

    this.memoizedOrgsInSyncing = memoize(workspaceOrgDatasourcesInSyncing);
    this.memoizedProcessWorkspace = memoize(processWorkspace);

    this.subscribeToObservers();
  }

  // ***************
  // *  Public API *
  // ***************

  public get isLoading() {
    return [this.currentUserObs, this.consolidationsObs, this.integrationsObs, this.workspacesObs].some(
      observer => observer.getCurrentResult().isPending
    );
  }

  // ****************************
  // *  Private / Protected API *
  // ****************************

  protected get currentUser() {
    return this.getResult(this.currentUserObs).data;
  }

  protected get datasourceIDsInSyncing() {
    const ws = this.rawWorkspace;
    return ws ? this.memoizedOrgsInSyncing([ws]) : [];
  }

  protected get integrations() {
    return this.getResult(this.integrationsObs).data;
  }

  protected get hasMembersPermission() {
    return checkNavPermission(this.rawWorkspace?.permissions ?? null, ROUTES.MEMBERS_ROUTE);
  }

  protected get workspacesDSPollData() {
    return this.getResult(this.workspacesDSPollDataObs).data;
  }

  protected get rawWorkspaces() {
    return this.getResult(this.workspacesObs).data;
  }

  protected get rawWorkspace() {
    return this.rawWorkspaces?.find(w => w.id === this.workspaceId);
  }

  protected get processedWorkspace() {
    const rawWorkspaces = this.rawWorkspaces;
    const consolidations = this.consolidations;
    const integrations = this.integrations;
    const currentUser = this.currentUser;
    if (!rawWorkspaces || !consolidations || !integrations || !currentUser) {
      return undefined;
    }
    const rawWorkspace = this.rawWorkspace;
    if (!rawWorkspace) {
      return undefined;
    }
    return this.memoizedProcessWorkspace(rawWorkspace, integrations, currentUser, consolidations, this.flags);
  }

  protected updateDependentQueries() {
    if (this.pollingEnabled) {
      // Update workspaces with latest poll data
      this.updateWorkspacesFromPollData();

      // Update the observer for polling based on current orgs in syncing
      this.updateWorkspacePollDataObserver();
    }
  }

  protected updateWorkspacesFromPollData() {
    const pollData = this.workspacesDSPollData;
    const rawWorkspaces = this.rawWorkspaces;

    if (!pollData?.length || !rawWorkspaces?.length) {
      return;
    }

    const pollDataKey = pollData
      .map(item => `${item.id}:${item.sync_status}:${item.sync_status_modified}`)
      .sort()
      .join('|');

    if (pollDataKey !== this.appliedPollDataKey) {
      // Update each workspace's organisations with matching poll data
      const updatedWorkspaces = getUpdatedWorkspacesListItems(rawWorkspaces, pollData);

      // Persist updated workspaces to React Query cache
      queryClient.setQueryData(workspacesListingQueryKey(), updatedWorkspaces);
      this.appliedPollDataKey = pollDataKey;
    }
  }

  protected updateWorkspacePollDataObserver() {
    const datasourceIds = this.datasourceIDsInSyncing;
    const datasourceIdsKey = datasourceIds.join(',');

    if (datasourceIdsKey === this.loadedPollDatasourceIdsKey) {
      return;
    }

    this.workspacesDSPollDataObs.setOptions(workspacesDatasourcePollDataQueryConfig(datasourceIds));
    this.loadedPollDatasourceIdsKey = datasourceIdsKey;
  }

  protected get consolidations() {
    return this.getResult(this.consolidationsObs).data;
  }

  protected getNewState(): WorkspaceListViewState {
    return {
      ...super.getNewState(),
      consolidations: this.consolidations,
      currentUser: this.currentUser,
      hasMembersPermission: this.hasMembersPermission,
      integrations: this.integrations,
      workspace: this.processedWorkspace,
      workspaces: this.rawWorkspaces
    };
  }
}
